/**
 * Bookings query builder — the single translation point from filters to SQL.
 *
 * The table view, the row-count query and the CSV export ALL call this. That is
 * deliberate and load-bearing: the failure mode to avoid is a second, subtly
 * different filter implementation for export, which produces a CSV that
 * disagrees with the screen the operator was looking at. One builder makes
 * "export matches what you filtered" structural rather than something to
 * remember.
 *
 * Translation style follows `apps/builder/src/hooks/useLeads.ts`.
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@limo/database'
import type { BookingFilters, BookingSortField, TableSort } from '@limo/shared'
import { buildIlikeOrFilter } from '@limo/shared'

/**
 * The FK the attribution embed travels along.
 *
 * `booking_quotes` has TWO foreign keys into `bookings` — `booking_id` (the
 * outbound leg) and `return_booking_id` (a round trip's return leg, added by
 * migration 20260416200100). PostgREST refuses to guess between them and fails
 * the whole request with PGRST201 ("more than one relationship was found"),
 * which is what a bare `booking_quotes` embed does now. Naming the constraint
 * is the documented disambiguation.
 *
 * `booking_id` is the correct one: it is the leg the quote was created for, and
 * the leg that carries the attribution the operator is filtering on.
 */
const ATTRIBUTION_FK = 'booking_quotes_booking_id_fkey'

/** Columns the free-text search box covers. */
const SEARCH_COLUMNS = [
  'customer_name',
  'customer_email',
  'confirmation_code',
  'pickup_address',
] as const

export interface BuildQueryOptions {
  /** Ask PostgREST for an exact total alongside the rows. */
  readonly count?: boolean
  /** Count only — no row payload. Used by the KPI cards. */
  readonly headOnly?: boolean
}

/**
 * True when any marketing-attribution filter is active.
 *
 * This gates the join below and is the single most important predicate in this
 * file — see the comment on `selectFor`.
 */
export function hasAttributionFilter(filters: BookingFilters): boolean {
  return (
    (filters.utmSource?.length ?? 0) > 0 ||
    (filters.utmMedium?.length ?? 0) > 0 ||
    (filters.utmCampaign?.length ?? 0) > 0
  )
}

/**
 * Build the PostgREST select string.
 *
 * ⚠ `bookings` has NO utm_* columns of its own — they live only on
 * `booking_quotes`, reachable via the `booking_quotes.booking_id` FK. Filtering
 * bookings by attribution therefore requires an embedded resource, and to make
 * the embed act as a FILTER (rather than just extra data) it must be `!inner`.
 *
 * `!inner` EXCLUDES every booking with no matching quote row — phone-in
 * bookings, rows predating the quotes feature, rows whose quote was deleted. If
 * it were applied unconditionally, the unfiltered list would silently shrink:
 * an account with 500 bookings and 80 phone-ins would show 420 and under-report
 * revenue by ~16%, with no error and no visual cue. That is indistinguishable
 * from real data, which makes it the highest-risk defect in this feature.
 *
 * So: the join is added ONLY when an attribution filter is actually set. Do not
 * "simplify" this by always embedding with !inner.
 *
 * The embed must also name its FK (see `ATTRIBUTION_FK`) or PostgREST rejects
 * the request outright.
 */
function selectFor(filters: BookingFilters): string {
  if (!hasAttributionFilter(filters)) {
    return '*, vehicles (name)'
  }
  return `*, vehicles (name), booking_quotes!${ATTRIBUTION_FK}!inner (utm_source, utm_medium, utm_campaign)`
}

/** Sortable columns, mapped to their real database column names. */
const SORT_COLUMNS: Record<BookingSortField, string> = {
  created_at: 'created_at',
  pickup_datetime: 'pickup_datetime',
  total_amount: 'total_amount',
  balance_due: 'balance_due',
  customer_name: 'customer_name',
  booking_status: 'booking_status',
  payment_status: 'payment_status',
}

type Client = SupabaseClient<Database>

/**
 * Compose a filtered bookings query.
 *
 * Returns the PostgREST builder without awaiting it, so callers can add
 * `.range()` (list), read `count` (totals), or loop batches (export).
 */
export function buildBookingsQuery(
  client: Client,
  accountId: string,
  filters: BookingFilters,
  sort?: TableSort<BookingSortField>,
  options: BuildQueryOptions = {}
) {
  const { count = false, headOnly = false } = options

  let query = client
    .from('bookings')
    .select(selectFor(filters), {
      ...(count || headOnly ? { count: 'exact' as const } : {}),
      ...(headOnly ? { head: true } : {}),
    })
    .eq('account_id', accountId)
    // Soft-deleted bookings (admin_soft_delete_quote) are hidden everywhere
    .is('deleted_at', null)

  // --- Test bookings -------------------------------------------------------
  // Excluded unless explicitly opted in. Onboarding smoke tests create real
  // rows with is_test = true; letting them into a revenue report is worse than
  // having no report.
  if (filters.includeTest !== true) {
    query = query.eq('is_test', false)
  }

  // --- Free text -----------------------------------------------------------
  // Escaping happens in @limo/shared so it is unit-testable; returns null when
  // there is nothing to search, in which case we omit .or() entirely rather
  // than emitting a filter that matches everything.
  const searchFilter = buildIlikeOrFilter(filters.search, SEARCH_COLUMNS)
  if (searchFilter !== null) {
    query = query.or(searchFilter)
  }

  // --- Dates ---------------------------------------------------------------
  // Half-open [from, to): gte + lt. See DateRangeFilter's docs — an inclusive
  // upper bound needs a 23:59:59.999 fencepost that breaks across DST.
  if (filters.pickupDate?.from) query = query.gte('pickup_datetime', filters.pickupDate.from)
  if (filters.pickupDate?.to) query = query.lt('pickup_datetime', filters.pickupDate.to)
  if (filters.createdDate?.from) query = query.gte('created_at', filters.createdDate.from)
  if (filters.createdDate?.to) query = query.lt('created_at', filters.createdDate.to)

  // --- Status --------------------------------------------------------------
  if (filters.bookingStatus?.length) {
    query = query.in('booking_status', [...filters.bookingStatus])
  }
  if (filters.paymentStatus?.length) {
    query = query.in('payment_status', [...filters.paymentStatus])
  }

  // --- Money ---------------------------------------------------------------
  if (filters.amountMin !== undefined) query = query.gte('total_amount', filters.amountMin)
  if (filters.amountMax !== undefined) query = query.lte('total_amount', filters.amountMax)

  // Generated column (total_amount - COALESCE(amount_paid, 0)). The COALESCE in
  // the migration is what makes legacy rows with a NULL amount_paid match here.
  if (filters.hasBalanceDue === true) query = query.gt('balance_due', 0)

  // --- Trip ----------------------------------------------------------------
  if (filters.serviceType?.length) {
    query = query.in('service_type', [...filters.serviceType])
  }
  if (filters.vehicleIds?.length) {
    query = query.in('vehicle_id', [...filters.vehicleIds])
  }
  if (filters.passengerMin !== undefined) {
    query = query.gte('passenger_count', filters.passengerMin)
  }
  if (filters.passengerMax !== undefined) {
    query = query.lte('passenger_count', filters.passengerMax)
  }
  if (filters.roundTrip !== undefined) query = query.eq('return_trip', filters.roundTrip)
  if (filters.airportPickup !== undefined) {
    query = query.eq('is_airport_pickup', filters.airportPickup)
  }
  if (filters.airportDropoff !== undefined) {
    query = query.eq('is_airport_dropoff', filters.airportDropoff)
  }

  // --- Attribution ---------------------------------------------------------
  // Only reachable when selectFor() added the !inner embed above; the guard
  // keeps the two in lockstep.
  if (hasAttributionFilter(filters)) {
    if (filters.utmSource?.length) {
      query = query.in('booking_quotes.utm_source', [...filters.utmSource])
    }
    if (filters.utmMedium?.length) {
      query = query.in('booking_quotes.utm_medium', [...filters.utmMedium])
    }
    if (filters.utmCampaign?.length) {
      query = query.in('booking_quotes.utm_campaign', [...filters.utmCampaign])
    }
  }

  // --- Sort ----------------------------------------------------------------
  const sortColumn = sort ? SORT_COLUMNS[sort.field] : 'created_at'
  query = query.order(sortColumn, { ascending: sort?.direction === 'asc' })

  // Stable tie-break so pagination can't repeat or skip a row when the sort
  // column has duplicates (many bookings share a pickup date).
  if (sortColumn !== 'id') {
    query = query.order('id', { ascending: false })
  }

  return query
}
